import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import Header from './Header';
import NativeAd from './NativeAd';
import NativeAdSection from './NativeAdSection';
import AdZone from './AdZone';
import VastPopupPlayer from './VastPopupPlayer';
import Footer from './Footer';
import { useTracking } from '../hooks/useTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Banner Ad Component (Simplified for sidebar only)
const BannerAd = ({ placement = "video-sidebar", className = "" }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBannerAds();
  }, [placement]);

  const fetchBannerAds = async () => {
    try {
      const response = await axios.get(`${API}/ads/banner/${placement}`);
      setAds(response.data);
    } catch (error) {
      console.error('Failed to fetch banner ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdClick = async () => {
    if (ads.length === 0) return;
    
    const ad = ads[0];
    try {
      await axios.post(`${API}/ads/${ad.id}/click`);
      if (ad.click_url) {
        window.open(ad.click_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Failed to track ad click:', error);
    }
  };

  if (loading) {
    return (
      <div className={`loading-skeleton rounded-lg ${className}`}>
        <div className="flex items-center justify-center h-full p-4">
          <div className="animate-pulse text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  if (ads.length === 0) {
    return (
      <div className={`glass-effect rounded-lg flex items-center justify-center border-2 border-dashed border-orange-500/20 ${className}`}>
        <div className="text-center p-4">
          <div className="text-3xl mb-2 text-orange-400">📺</div>
          <div className="text-orange-400 font-medium">Premium Ad Space</div>
          <div className="text-gray-500 text-sm">300 × 250</div>
        </div>
      </div>
    );
  }

  const ad = ads[0];

  return (
    <div className={`glass-effect rounded-lg overflow-hidden ${ad.iframe_code ? '' : 'cursor-pointer group'} ${className}`} 
         onClick={ad.iframe_code ? undefined : handleAdClick}
         data-testid={`banner-ad-${placement}`}>
      {ad.iframe_code ? (
        <div 
          className="w-full h-full"
          dangerouslySetInnerHTML={{ __html: ad.iframe_code }}
        />
      ) : ad.banner_url ? (
        <div className="relative">
          <img 
            src={ad.banner_url} 
            alt={ad.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-2 right-2">
            <Badge className="bg-orange-600/90 text-white text-xs">Ad</Badge>
          </div>
        </div>
      ) : (
        <div className="h-full flex items-center justify-center p-6 text-center">
          <div>
            <div className="text-3xl mb-2 text-orange-400">🎯</div>
            <div className="text-white font-medium">{ad.name}</div>
            <div className="text-orange-400 text-sm mt-1">Premium Advertisement</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Video Card Component for Recommendations
const VideoCard = ({ video, onVideoClick }) => {
  return (
    <Card 
      className="bg-gray-800 border-gray-700 hover:border-red-500/50 transition-all duration-300 cursor-pointer group overflow-hidden"
      onClick={() => onVideoClick(video.id)}
      data-testid={`recommended-video-${video.id}`}
    >
      <div className="relative aspect-video">
        <img
          src={video.thumbnail_url || `https://picsum.photos/300/169?random=${video.id}`}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs text-white backdrop-blur-sm">
          {video.duration ? `${Math.floor(video.duration / 60)}:${(video.duration % 60).toString().padStart(2, '0')}` : '10:30'}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center animate-bounce">
            <div className="w-0 h-0 border-l-[15px] border-l-white border-y-[10px] border-y-transparent ml-1" />
          </div>
        </div>
        {/* HD Badge removed as requested */}
      </div>
      <CardContent className="p-3">
        <h4 className="text-white font-medium mb-1 line-clamp-2 text-sm group-hover:text-orange-400 transition-colors">
          {video.title}
        </h4>
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>{video.view_count.toLocaleString()} views</span>
          <div className="flex items-center space-x-1">
            <span className="text-orange-400">⭐</span>
            <span>{video.rating}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const VideoPlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [recommendedVideos, setRecommendedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vastAd, setVastAd] = useState(null);
  const [showVastPopup, setShowVastPopup] = useState(false);
  const [showAd, setShowAd] = useState(true);
  const [adSkippable, setAdSkippable] = useState(false);
  const [adCountdown, setAdCountdown] = useState(5);
  
  // Initialize tracking
  const { trackVideoView } = useTracking();

  useEffect(() => {
    if (id) {
      fetchVideo();
      fetchVastAd();
    }
  }, [id]);

  useEffect(() => {
    if (video) {
      fetchRecommendedVideos();
      trackVideoView(video.id);
    }
  }, [video]);

  // Ad countdown timer
  useEffect(() => {
    if (showAd && adCountdown > 0) {
      const timer = setTimeout(() => {
        setAdCountdown(adCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showAd && adCountdown === 0) {
      setAdSkippable(true);
    }
  }, [showAd, adCountdown]);

  const fetchVideo = async () => {
    try {
      const response = await axios.get(`${API}/videos/${id}`);
      setVideo(response.data);
    } catch (error) {
      console.error('Failed to fetch video:', error);
      navigate('/');
    }
  };

  const fetchVastAd = async () => {
    try {
      const response = await axios.get(`${API}/ads/vast/preroll`);
      setVastAd(response.data);
      
      // Show VAST popup player when ad is loaded
      if (response.data && response.data.vast_url) {
        setShowVastPopup(true);
      }
      
      setShowAd(true);
      setAdCountdown(5);
      setAdSkippable(false);
    } catch (error) {
      console.error('Failed to fetch VAST ad:', error);
      setShowAd(false);
      setShowVastPopup(false);
    }
  };

  const handleVastPopupClose = () => {
    setShowVastPopup(false);
    setShowAd(false);
    console.log('VAST popup closed - main video can now play');
  };

  const fetchRecommendedVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos/${id}/recommended?limit=8`);
      setRecommendedVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch recommended videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (videoId) => {
    navigate(`/video/${videoId}`);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleSkipAd = () => {
    setShowAd(false);
  };

  const handleAdClick = () => {
    if (vastAd && vastAd.click_url) {
      window.open(vastAd.click_url, '_blank', 'noopener,noreferrer');
    }
    // Track ad click
    if (vastAd) {
      axios.post(`${API}/ads/${vastAd.id}/click`).catch(console.error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading video...</div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎬</div>
          <div className="text-xl text-gray-400 mb-4">Video not found</div>
          <Button onClick={handleBackToHome} className="bg-red-600 hover:bg-red-700">
            Back to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="video-player-page">
      {/* VAST Popup Player */}
      {showVastPopup && vastAd && (
        <VastPopupPlayer 
          vastUrl={vastAd.vast_url}
          onClose={handleVastPopupClose}
        />
      )}
      
      {/* Header with sticky functionality */}
      <Header showNavigation={true} />
      
      {/* Breadcrumb Navigation */}
      <div className="bg-gray-900/50">
        <div className="container mx-auto px-6 py-3">
          <Button
            onClick={handleBackToHome}
            variant="ghost"
            className="text-gray-300 hover:text-orange-400 hover:bg-orange-600/10 text-sm"
            data-testid="back-home-btn"
          >
            ← Back to Home
          </Button>
        </div>
      </div>

      <main className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Video Area */}
          <div className="lg:col-span-3">
            {/* Video Player */}
            <div className="bg-black rounded-lg overflow-hidden mb-6" data-testid="video-player-container">
              <div className="relative aspect-video">
                {/* Main video iframe */}
                <iframe
                  src={video.embed_url}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={video.title}
                />
              </div>
            </div>

            {/* Video Info */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold title-primary mb-4" data-testid="video-title">
                {video.title}
              </h1>
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-6 text-gray-400">
                  <span className="flex items-center space-x-2">
                    <span className="text-orange-400">👀</span>
                    <span>{video.view_count.toLocaleString()} views</span>
                  </span>
                  <span className="flex items-center space-x-2">
                    <span className="text-orange-400">⭐</span>
                    <span>{video.rating} rating</span>
                  </span>
                  <span className="flex items-center space-x-2">
                    <span className="text-orange-400">⏱️</span>
                    <span>
                      {video.duration ? 
                        `${Math.floor(video.duration / 60)}:${(video.duration % 60).toString().padStart(2, '0')}` : 
                        '10:30'
                      }
                    </span>
                  </span>
                </div>
              </div>

              {video.description && (
                <div className="glass-effect rounded-lg p-4 mb-4">
                  <p className="text-gray-300 leading-relaxed">{video.description}</p>
                </div>
              )}

              {video.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {video.tags.map((tag, index) => (
                    <Badge key={index} className="bg-orange-600/20 text-orange-300 border-orange-500/30 hover:bg-orange-600/30 cursor-pointer transition-colors">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Copyright Disclaimer */}
              <div className="disclaimer">
                <p className="text-xs text-gray-300 mb-2">
                  {video.embed_url ? (
                    <a 
                      href={(() => {
                        try {
                          const url = new URL(video.embed_url);
                          return `${url.protocol}//${url.hostname}`;
                        } catch {
                          return video.embed_url;
                        }
                      })()} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="disclaimer-link"
                      data-testid="contact-owner-link"
                    >
                      Click here to contact the original owner of this video.
                    </a>
                  ) : (
                    <span className="text-gray-400">Source information not available.</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar with Ads and Recommendations */}
          <div className="lg:col-span-1">
            {/* Sidebar Ads - Two stacked 300x250 banners */}
            <div className="space-y-6 mb-8">
              {/* Top sidebar banner - 300x250 */}
              <AdZone 
                placementId="video_sidebar_top_300x250" 
                className="w-[300px] h-[250px] mx-auto" 
                fallback={
                  <div className="glass-effect rounded-lg flex items-center justify-center border-2 border-dashed border-orange-500/20 w-[300px] h-[250px] mx-auto">
                    <div className="text-center p-4">
                      <div className="text-3xl mb-2 text-orange-400">📺</div>
                      <div className="text-orange-400 font-medium">Top Ad Space</div>
                      <div className="text-gray-500 text-sm">300 × 250</div>
                    </div>
                  </div>
                }
              />
              {/* Bottom sidebar banner - 300x250 */}
              <AdZone 
                placementId="video_sidebar_bottom_300x250" 
                className="w-[300px] h-[250px] mx-auto" 
                fallback={
                  <div className="glass-effect rounded-lg flex items-center justify-center border-2 border-dashed border-orange-500/20 w-[300px] h-[250px] mx-auto">
                    <div className="text-center p-4">
                      <div className="text-3xl mb-2 text-orange-400">📺</div>
                      <div className="text-orange-400 font-medium">Bottom Ad Space</div>
                      <div className="text-gray-500 text-sm">300 × 250</div>
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        </div>

        <Separator className="bg-orange-500/20 mb-8" />

        {/* Recommended Videos */}
        <section>
          <h2 className="text-2xl font-bold title-primary mb-6 flex items-center">
            <span className="mr-3 text-orange-400">💡</span>
            Recommended for You
          </h2>
          
          {recommendedVideos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {recommendedVideos.map((recVideo) => (
                <VideoCard key={recVideo.id} video={recVideo} onVideoClick={handleVideoClick} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-4xl mb-2 text-orange-400">🎯</div>
              <div className="text-gray-400">No recommendations available</div>
            </div>
          )}
        </section>

        {/* Global Native Ad Section */}
        <div className="section-spacing">
          <NativeAdSection />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VideoPlayer;